/* SIZESOFTX
=========================================================================================
* Descripción: Invitaciones de licencia con contrato de uso: listado/seguimiento,
* creación (aparta la licencia), envío/reenvío de contrato, cancelación y timeline.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 15/09/2026 | José Luis Aragón Cervantes       | Creación
*      2       | 15/09/2026 | José Luis Aragón Cervantes       | Validación en vivo de licencia manual
* ---------------------------------------------------------------------------------------
*/

import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  Email as EmailIcon,
  EventNote as EventNoteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  apiInvitacionesDisponibles,
  apiInvitacionesList,
  apiInvitacionCancelar,
  apiInvitacionDetalle,
  apiInvitacionCrear,
  apiInvitacionEnviarContrato,
  apiInvitacionReenviar,
  apiInvitacionValidarLicencia,
} from "../settings/supportApi";
import {
  EMAIL_REGEX,
  sanitizarEmail,
  sanitizarTexto,
  sanitizarTelefono,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const ESTADOS = [
  { value: "TODAS", label: "Todas", color: "default" },
  { value: "PENDIENTE", label: "Pendiente", color: "warning" },
  { value: "ENVIADA", label: "Contrato enviado", color: "info" },
  { value: "CONTRATO_ACEPTADO", label: "Contrato aceptado", color: "success" },
  { value: "CLAVE_ENVIADA", label: "Clave enviada", color: "primary" },
  { value: "ACTIVADA", label: "Activada", color: "success" },
  { value: "EXPIRADA", label: "Expirada", color: "error" },
  { value: "CANCELADA", label: "Cancelada", color: "error" },
];

const colorEstado = (estado) => {
  const e = ESTADOS.find((x) => x.value === estado);
  return e ? e.color : "default";
};

const TENANT_ACTIVATION_URL = process.env.REACT_APP_TenantActivationUrl || "";

const ESTADOS_CON_ENVIO = ["PENDIENTE", "ENVIADA", "CONTRATO_ACEPTADO", "CLAVE_ENVIADA"];

const textoAccionEnvio = (estado) => {
  if (estado === "PENDIENTE") return "Enviar invitación";
  if (estado === "ENVIADA") return "Reenviar invitación";
  if (estado === "CONTRATO_ACEPTADO") return "Enviar clave";
  if (estado === "CLAVE_ENVIADA") return "Reenviar clave";
  return "Enviar";
};

const mensajeAccionEnvio = (estado) => {
  if (estado === "PENDIENTE") return "Invitación enviada.";
  if (estado === "ENVIADA") return "Invitación reenviada.";
  if (estado === "CONTRATO_ACEPTADO") return "Clave de activación enviada.";
  if (estado === "CLAVE_ENVIADA") return "Clave de activación reenviada.";
  return "Proceso de envío ejecutado.";
};

const fmtFecha = (value) =>
  value ? new Date(value).toLocaleDateString("es-MX") + " " + new Date(value).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : "—";

const LONGITUD_LICENCIA = 19;
const LICENCIA_REGEX = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

const formatearLicencia = (value) => {
  const limpia = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 16);
  const partes = limpia.match(/.{1,4}/g);
  return partes ? partes.join("-") : "";
};

const estadoInicialForm = {
  tenantActivationLicenseId: "",
  licenseCode: "",
  razonSocial: "",
  nombreComercial: "",
  rfc: "",
  nombreContacto: "",
  emailContacto: "",
  telefonoContacto: "",
  notas: "",
  validezDias: 15,
};

const InvitacionesPage = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [estado, setEstado] = useState("TODAS");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(0);
  const [tamano, setTamano] = useState(20);
  const [total, setTotal] = useState(0);

  const [openCrear, setOpenCrear] = useState(false);
  const [licencias, setLicencias] = useState([]);
  const [cargandoLicencias, setCargandoLicencias] = useState(false);
  const [form, setForm] = useState(estadoInicialForm);
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState("");
  const [validandoLicencia, setValidandoLicencia] = useState(false);
  const [licenciaValidada, setLicenciaValidada] = useState(null);
  const [mensajeLicencia, setMensajeLicencia] = useState("");

  const [detalle, setDetalle] = useState(null);
  const [openDetalle, setOpenDetalle] = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [motivoCancelar, setMotivoCancelar] = useState("");
  const [accion, setAccion] = useState(""); // fila en acción

  const cargar = () => {
    const busquedaLimpia = sanitizarTexto(busqueda, 120);
    apiInvitacionesList({ pagina: pagina + 1, tamano, estado, busqueda: busquedaLimpia || undefined })
      .then((data) => {
        setRows(data?.items || []);
        setTotal(data?.total || 0);
      })
      .catch((err) => setError(err.message));
  };

  const cargarLicencias = () => {
    setCargandoLicencias(true);
    setErrorCrear("");
    apiInvitacionesDisponibles({ limite: 100 })
      .then((data) => setLicencias(data || []))
      .catch((err) => setErrorCrear("No se pudieron cargar las licencias disponibles: " + err.message))
      .finally(() => setCargandoLicencias(false));
  };

  useEffect(() => {
    cargar();
  }, [estado, busqueda, pagina, tamano]);

  const abrirCrear = () => {
    setForm(estadoInicialForm);
    setErrorCrear("");
    setLicenciaValidada(null);
    setMensajeLicencia("");
    setOpenCrear(true);
    cargarLicencias();
  };

  useEffect(() => {
    if (!openCrear) return undefined;

    const codigo = (form.licenseCode || "").trim();
    setLicenciaValidada(null);

    if (!codigo) {
      setMensajeLicencia("");
      setValidandoLicencia(false);
      return undefined;
    }

    if (codigo.length < LONGITUD_LICENCIA) {
      setMensajeLicencia(`Faltan ${LONGITUD_LICENCIA - codigo.length} caracteres para validar la licencia.`);
      setValidandoLicencia(false);
      return undefined;
    }

    if (!LICENCIA_REGEX.test(codigo)) {
      setMensajeLicencia("Formato inválido. Use XXXX-XXXX-XXXX-XXXX.");
      setValidandoLicencia(false);
      return undefined;
    }

    let cancelado = false;
    setValidandoLicencia(true);
    setMensajeLicencia("Validando licencia...");
    const timer = setTimeout(() => {
      apiInvitacionValidarLicencia(codigo)
        .then((licencia) => {
          if (cancelado) return;
          setLicenciaValidada(licencia);
          setMensajeLicencia(`Licencia validada: ${licencia.licenseCode} · ${licencia.planNombre || licencia.licenseType || "plan sin nombre"}.`);
          setForm((f) => {
            if ((f.licenseCode || "").trim() !== codigo) return f;
            return {
              ...f,
              tenantActivationLicenseId: licencia.tenantActivationLicenseId,
              licenseCode: licencia.licenseCode,
              razonSocial: f.razonSocial || licencia.issuedToName || "",
              nombreContacto: f.nombreContacto || licencia.issuedToName || "",
              emailContacto: f.emailContacto || licencia.issuedToEmail || "",
            };
          });
        })
        .catch((err) => {
          if (cancelado) return;
          setLicenciaValidada(null);
          setMensajeLicencia(err.message || "No fue posible validar la licencia.");
          setForm((f) => ((f.licenseCode || "").trim() === codigo ? { ...f, tenantActivationLicenseId: "" } : f));
        })
        .finally(() => {
          if (!cancelado) setValidandoLicencia(false);
        });
    }, 450);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [openCrear, form.licenseCode]);

  const crearInvitacion = () => {
    const licenseCode = (form.licenseCode || "").trim().toUpperCase();
    if (!licenseCode) {
      setErrorCrear("Escriba el código de licencia.");
      return;
    }
    if (licenseCode.length !== LONGITUD_LICENCIA || !LICENCIA_REGEX.test(licenseCode)) {
      setErrorCrear("La licencia debe tener 19 caracteres con guiones: XXXX-XXXX-XXXX-XXXX.");
      return;
    }
    const razonSocial = sanitizarTexto(form.razonSocial, 180);
    const nombreComercial = sanitizarTexto(form.nombreComercial, 180);
    const rfc = sanitizarTexto(form.rfc, 20).replace(/\s+/g, "").toUpperCase();
    const nombreContacto = sanitizarTexto(form.nombreContacto, 120);
    const emailContacto = sanitizarEmail(form.emailContacto, 120);
    const telefonoContacto = sanitizarTelefono(form.telefonoContacto, 25);
    const notas = sanitizarTexto(form.notas, 500);
    if (!razonSocial) {
      setErrorCrear("Razón social requerida.");
      return;
    }
    if (!nombreContacto) {
      setErrorCrear("Nombre de contacto requerido.");
      return;
    }
    if (!emailContacto || !EMAIL_REGEX.test(emailContacto)) {
      setErrorCrear("Correo de contacto inválido.");
      return;
    }
    const bloqueo = validarSinInyeccion([
      { etiqueta: "Razón social", valor: form.razonSocial },
      { etiqueta: "Nombre comercial", valor: form.nombreComercial },
      { etiqueta: "RFC", valor: form.rfc },
      { etiqueta: "Nombre de contacto", valor: form.nombreContacto },
      { etiqueta: "Correo de contacto", valor: form.emailContacto },
      { etiqueta: "Teléfono", valor: form.telefonoContacto },
      { etiqueta: "Notas", valor: form.notas },
    ]);
    if (bloqueo) {
      setErrorCrear(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    const payload = {
      ...form,
      licenseCode,
      razonSocial,
      nombreComercial,
      rfc,
      nombreContacto,
      emailContacto,
      telefonoContacto,
      notas,
    };
    if (!payload.tenantActivationLicenseId) {
      delete payload.tenantActivationLicenseId;
    }
    setCreando(true);
    setErrorCrear("");
    apiInvitacionCrear(payload)
      .then(async (resumen) => {
        const invitacionId = resumen?.invitacionId;
        if (!invitacionId) {
          throw new Error("La invitación se creó, pero la respuesta no incluyó el ID para enviar el contrato.");
        }

        try {
          const envio = await apiInvitacionEnviarContrato(invitacionId);
          setNotice(
            "Invitación creada y contrato enviado a " +
              (envio?.destinatario || resumen.emailContacto || form.emailContacto) +
              " (licencia " +
              resumen.licenseCode +
              ")."
          );
        } catch (envioError) {
          setError(
            "La invitación se creó y la licencia quedó apartada, pero no se pudo enviar el contrato: " +
              envioError.message +
              ". Puedes reintentar con el botón de envío en la lista."
          );
        }

        setOpenCrear(false);
        setPagina(0);
        cargar();
      })
      .catch((err) => setErrorCrear(err.message))
      .finally(() => setCreando(false));
  };

  const ejecutar = (accionName, promesa, okMsg) => {
    setAccion(accionName);
    setError("");
    setNotice("");
    promesa
      .then((r) => {
        setNotice(okMsg + (r?.mensaje ? " — " + r.mensaje : ""));
        cargar();
      })
      .catch((err) => setError(err.message))
      .finally(() => setAccion(""));
  };

  const verDetalle = (id) => {
    setOpenDetalle(true);
    setCargandoDetalle(true);
    setDetalle(null);
    apiInvitacionDetalle(id)
      .then(setDetalle)
      .catch((err) => setError(err.message))
      .finally(() => setCargandoDetalle(false));
  };

  const cancelar = (id) => {
    const motivo = sanitizarTexto(motivoCancelar, 500);
    const bloqueo = validarSinInyeccion([{ etiqueta: "Motivo de cancelación", valor: motivoCancelar }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    ejecutar("cancel-" + id, apiInvitacionCancelar(id, { motivo }), "Invitación cancelada.");
    setMotivoCancelar("");
    setOpenDetalle(false);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Invitaciones de licencia · Contrato de uso</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCrear}>
          Nueva invitación
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Estado"
          value={estado}
          onChange={(e) => { setEstado(e.target.value); setPagina(0); }}
          sx={{ minWidth: 200 }}
        >
          {ESTADOS.map((e) => (
            <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Buscar (razón social, RFC, correo, licencia)"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
      </Stack>

      {rows === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Box} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, background: "#f6f8fb" } }}>
                <TableCell>Licencia / Plan</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Sin invitaciones con los filtros actuales.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const ocupado = Boolean(accion);
                  const cancelable = !["ACTIVADA", "CANCELADA"].includes(row.estadoInvitacion);
                  const puedeEnviar = ESTADOS_CON_ENVIO.includes(row.estadoInvitacion);
                  const etiquetaEnvio = textoAccionEnvio(row.estadoInvitacion);
                  return (
                    <TableRow key={row.invitacionId} hover>
                      <TableCell>
                        <b>{row.licenseCode}</b>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.planNombre || "—"} · v{row.versionContrato}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.razonSocial}
                        {row.nombreComercial && row.nombreComercial !== row.razonSocial && (
                          <Typography variant="caption" display="block" color="text.secondary">{row.nombreComercial}</Typography>
                        )}
                        {row.rfc && <Typography variant="caption" display="block" color="text.secondary">{row.rfc}</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.nombreContacto}
                        <Typography variant="caption" display="block" color="text.secondary">{row.emailContacto}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={colorEstado(row.estadoInvitacion)} label={row.estadoInvitacion} />
                      </TableCell>
                      <TableCell>{fmtFecha(row.creadaUtc)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                          <Tooltip title="Detalle / seguimiento">
                            <IconButton size="small" onClick={() => verDetalle(row.invitacionId)}>
                              <EventNoteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {puedeEnviar && (
                            <Tooltip title={etiquetaEnvio + " al correo registrado"}>
                              <span>
                                <Button
                                  size="small"
                                  variant={row.estadoInvitacion === "PENDIENTE" ? "contained" : "outlined"}
                                  startIcon={<EmailIcon fontSize="small" />}
                                  disabled={ocupado}
                                  onClick={() =>
                                    ejecutar(
                                      "envio-" + row.invitacionId,
                                      apiInvitacionReenviar(row.invitacionId),
                                      mensajeAccionEnvio(row.estadoInvitacion)
                                    )
                                  }
                                >
                                  {etiquetaEnvio}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {cancelable && (
                            <Tooltip title="Cancelar invitación y liberar licencia">
                              <IconButton size="small" disabled={ocupado} onClick={() => { setMotivoCancelar(""); verDetalle(row.invitacionId); }}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            rowsPerPage={tamano}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_, p) => setPagina(p)}
            onRowsPerPageChange={(e) => { setTamano(parseInt(e.target.value, 10)); setPagina(0); }}
            labelRowsPerPage="Por página"
          />
        </TableContainer>
      )}

      {/* Diálogo: nueva invitación */}
      <Dialog open={openCrear} onClose={() => setOpenCrear(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva invitación con contrato de uso</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errorCrear && <Alert severity="error">{errorCrear}</Alert>}
            <Alert severity="info">
              Al crear se envía el contrato. Cuando el cliente lo acepte, Support enviará una clave nueva y la liga de activación de Tenant{TENANT_ACTIVATION_URL ? `: ${TENANT_ACTIVATION_URL}` : "."}
            </Alert>
            <Autocomplete
              options={licencias}
              loading={cargandoLicencias}
              getOptionLabel={(l) =>
                `${l.licenseCode} · ${l.planNombre || l.licenseType} · ${l.contractPeriod} · ${l.estatus}` +
                (l.issuedToName ? ` · ${l.issuedToName}` : "")
              }
              onChange={(_, l) => {
                setLicenciaValidada(l || null);
                setMensajeLicencia(l ? `Licencia validada: ${l.licenseCode} · ${l.planNombre || l.licenseType || "plan sin nombre"}.` : "");
                setForm((f) => ({
                  ...f,
                  tenantActivationLicenseId: l ? l.tenantActivationLicenseId : "",
                  licenseCode: l ? l.licenseCode : f.licenseCode,
                  razonSocial: l ? (f.razonSocial || l.issuedToName || "") : f.razonSocial,
                  nombreContacto: l ? (f.nombreContacto || l.issuedToName || "") : f.nombreContacto,
                  emailContacto: l ? (f.emailContacto || l.issuedToEmail || "") : f.emailContacto,
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Buscar licencia disponible" placeholder="Opcional: seleccione de la lista" size="small" />
              )}
            />
            <TextField
              label="Código de licencia *"
              fullWidth
              size="small"
              value={form.licenseCode}
              onChange={(e) => setForm((f) => ({
                ...f,
                tenantActivationLicenseId: "",
                licenseCode: formatearLicencia(e.target.value),
              }))}
              error={Boolean(form.licenseCode) && form.licenseCode.length === LONGITUD_LICENCIA && !LICENCIA_REGEX.test(form.licenseCode)}
              helperText={`${form.licenseCode.length}/${LONGITUD_LICENCIA} caracteres con guiones. Formato: XXXX-XXXX-XXXX-XXXX.`}
              InputProps={{
                endAdornment: validandoLicencia ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
              }}
              placeholder="Ej. ARXS-9F58-3045-83EE"
            />
            {mensajeLicencia && (
              <Alert severity={licenciaValidada ? "success" : form.licenseCode.length === LONGITUD_LICENCIA ? "warning" : "info"}>
                {mensajeLicencia}
              </Alert>
            )}
            {licenciaValidada && (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, background: "#f8fbff" }}>
                <Typography variant="body2">
                  <b>{licenciaValidada.licenseCode}</b> · {licenciaValidada.planNombre || licenciaValidada.licenseType || "Plan sin nombre"} · {licenciaValidada.contractPeriod || "Periodo sin definir"}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Equipos: {licenciaValidada.maxDevices ?? "—"} · Usuarios: {licenciaValidada.maxUsers ?? "—"} · Estatus: {licenciaValidada.estatus || "—"}
                </Typography>
                {(licenciaValidada.issuedToName || licenciaValidada.issuedToEmail) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Datos sugeridos: {licenciaValidada.issuedToName || "sin nombre"} · {licenciaValidada.issuedToEmail || "sin correo"}
                  </Typography>
                )}
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Razón social *" fullWidth size="small" value={form.razonSocial}
                  onChange={(e) => setForm((f) => ({ ...f, razonSocial: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Nombre comercial" fullWidth size="small" value={form.nombreComercial}
                  onChange={(e) => setForm((f) => ({ ...f, nombreComercial: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="RFC" fullWidth size="small" value={form.rfc}
                  onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Vigencia de la invitación (días)" type="number" fullWidth size="small" value={form.validezDias}
                  onChange={(e) => setForm((f) => ({ ...f, validezDias: parseInt(e.target.value, 10) || 15 }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Nombre de contacto *" fullWidth size="small" value={form.nombreContacto}
                  onChange={(e) => setForm((f) => ({ ...f, nombreContacto: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Correo de contacto *" fullWidth size="small" value={form.emailContacto}
                  onChange={(e) => setForm((f) => ({ ...f, emailContacto: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Teléfono de contacto" fullWidth size="small" value={form.telefonoContacto}
                  onChange={(e) => setForm((f) => ({ ...f, telefonoContacto: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Notas" fullWidth size="small" value={form.notas}
                  onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCrear(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crearInvitacion} disabled={creando}>
            {creando ? <CircularProgress size={20} /> : "Crear y enviar contrato"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: detalle / seguimiento */}
      <Dialog open={openDetalle} onClose={() => setOpenDetalle(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de la invitación</DialogTitle>
        <DialogContent dividers>
          {cargandoDetalle ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : detalle ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Licencia</Typography>
                  <Typography variant="body1"><b>{detalle.licenseCode}</b> · {detalle.planNombre || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Estado</Typography>
                  <Chip size="small" color={colorEstado(detalle.estadoInvitacion)} label={detalle.estadoInvitacion} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Razón social</Typography>
                  <Typography variant="body1">{detalle.razonSocial}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Nombre comercial</Typography>
                  <Typography variant="body1">{detalle.nombreComercial || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contacto</Typography>
                  <Typography variant="body1">{detalle.nombreContacto} · {detalle.emailContacto}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">RFC / Teléfono</Typography>
                  <Typography variant="body1">{detalle.rfc || "—"} · {detalle.telefonoContacto || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Creada / Expira</Typography>
                  <Typography variant="body1">{fmtFecha(detalle.creadaUtc)} · {fmtFecha(detalle.expiraUtc)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contrato</Typography>
                  <Typography variant="body1">v{detalle.versionContrato} · {detalle.sPeriodoContrato || "—"} · {detalle.nMaxEquipos ?? "—"} equipos / {detalle.nMaxUsuarios ?? "—"} usuarios</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Aceptación</Typography>
                  <Typography variant="body1">
                    {detalle.sEstadoAceptacion ? detalle.sEstadoAceptacion + (detalle.dteAceptacionUtc ? " el " + fmtFecha(detalle.dteAceptacionUtc) : "") : "Sin aceptación"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Activación concluida</Typography>
                  <Typography variant="body1">{detalle.dteActivacionConcluidaUtc ? fmtFecha(detalle.dteActivacionConcluidaUtc) : "—"}</Typography>
                </Grid>
                {detalle.motivoCancelacion && (
                  <Grid item xs={12}>
                    <Alert severity="warning">Motivo de cancelación: {detalle.motivoCancelacion}</Alert>
                  </Grid>
                )}
              </Grid>
              <Divider />
              <Alert severity="info">
                Flujo de activación: primero el cliente acepta el contrato desde la invitación; después usa la clave enviada por correo en Tenant{TENANT_ACTIVATION_URL ? ` (${TENANT_ACTIVATION_URL})` : ""}. Si no le llegó la clave, usa el botón {detalle.estadoInvitacion === "CLAVE_ENVIADA" ? "Reenviar clave" : "Enviar clave"} en la lista.
                {TENANT_ACTIVATION_URL && (
                  <Button href={TENANT_ACTIVATION_URL} target="_blank" rel="noopener noreferrer" size="small" sx={{ ml: 1 }}>
                    Abrir Tenant
                  </Button>
                )}
              </Alert>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Seguimiento</Typography>
              {(detalle.eventos || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin eventos registrados.</Typography>
              ) : (
                detalle.eventos.map((ev, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="baseline">
                    <Chip size="small" variant="outlined" label={ev.evento} />
                    <Typography variant="body2">{ev.detalle}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtFecha(ev.fechaUtc)}{ev.actorEmail ? ` · ${ev.actorEmail}` : ""}
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detalle && !["ACTIVADA", "CANCELADA"].includes(detalle.estadoInvitacion) && (
            <>
              <TextField
                label="Motivo de cancelación (opcional)"
                size="small"
                value={motivoCancelar}
                onChange={(e) => setMotivoCancelar(e.target.value)}
                sx={{ mr: 1, minWidth: 260 }}
              />
              <Button color="error" startIcon={<CancelIcon />} onClick={() => cancelar(detalle.invitacionId)}>
                Cancelar invitación
              </Button>
            </>
          )}
          <Button onClick={() => setOpenDetalle(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvitacionesPage;
